"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { useRouter } from "next/navigation";

import { NOTIFICATION_CATEGORIES } from "@unreserved/constants";
import { useNotificationsInfinite } from "@unreserved/hooks";
import type { NotificationItemDto } from "@unreserved/sdk";
import { notificationsApi } from "@unreserved/services";
import { PopoverContent } from "@unreserved/ui/components/popover";
import { cn, formatDate } from "@unreserved/utils";

import { DismissCircleFilledIcon } from "@/app/_components/icons/dismiss-circle-filled";
import { NotificationItem } from "@/app/_components/notifications/notification-item";
import { Button } from "@/app/_components/shared/button";
import { CATEGORY_TO_TYPE, DATE_OPTIONS, handleNotificationClick } from "@/app/_libs/notifications";

export interface NotificationsPopoverContentProps {
  onClose?: () => void;
}

export function NotificationsPopoverContent({ onClose }: NotificationsPopoverContentProps) {
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useNotificationsInfinite();

  const notifications: NotificationItemDto[] =
    data?.pages.flatMap((page) => page.data?.notifications ?? []) ?? [];

  const typeFilter = CATEGORY_TO_TYPE[selectedCategory];
  const filtered = typeFilter
    ? notifications.filter((n) => n.notification_type === typeFilter)
    : notifications;

  const handleClick = useCallback(
    (item: NotificationItemDto) => {
      if (!item.is_read) {
        notificationsApi
          .notificationControllerMarkNotificationReadV1({ id: item.id })
          .then(() => refetch())
          .catch(console.error);
      }
      handleNotificationClick({ item, router, onClose });
    },
    [onClose, refetch, router],
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [sentinelNode, setSentinelNode] = useState<HTMLDivElement | null>(null);

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinelNode(node);
  }, []);

  useEffect(() => {
    const scrollRoot = scrollContainerRef.current;
    if (!scrollRoot || !sentinelNode || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollRoot, rootMargin: "100px" },
    );
    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [sentinelNode, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <PopoverContent
      className={cn(
        "text-typography-900 border-border-200 w-[calc(100vw-2rem)] max-w-[600px] gap-0 rounded-lg border bg-white p-0 shadow-[0px_6px_16px_0px_rgba(0,0,0,0.2)]",
      )}
      side="bottom"
      align="end"
      sideOffset={8}>
      <div className="flex items-center justify-between p-4">
        <h2 className="text-typography-900 text-base font-semibold">Notifications</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="shrink-0 cursor-pointer rounded-full"
          aria-label="Close">
          <DismissCircleFilledIcon fill="#999999" className="-mr-4 size-5" />
        </Button>
      </div>
      <div className="overflow-x-auto px-4 py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex gap-2">
          {NOTIFICATION_CATEGORIES.map((categoryTitle) => (
            <button
              key={categoryTitle}
              type="button"
              onClick={() => setSelectedCategory(categoryTitle || "All")}
              className={cn(
                "shrink-0 cursor-pointer rounded-full border px-3 py-1.5 text-sm font-medium",
                selectedCategory === categoryTitle
                  ? "bg-primary-500 border-primary-500 text-white"
                  : "border-border-200 text-typography-900 hover:bg-background-50 bg-white",
              )}>
              {categoryTitle}
            </button>
          ))}
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        className="mt-2 max-h-[min(60vh,400px)] overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="text-typography-500 text-sm">Loading…</div>
          </div>
        ) : filtered.length > 0 ? (
          <>
            {filtered.map((item, index) => (
              <NotificationItem
                key={item.id}
                title={String(item.subject ?? "")}
                message={String(item.message ?? item.subject ?? "")}
                date={formatDate(item.created_at, DATE_OPTIONS)}
                variant="row"
                onClick={() => handleClick(item)}
                isLast={index === filtered.length - 1}
                isRead={item.is_read}
              />
            ))}
            <div ref={sentinelRef} className="h-4" />
            {isFetchingNextPage && (
              <div className="text-typography-500 py-4 text-center text-sm">Loading…</div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-typography-500 py-6 text-sm">No notifications found</p>
          </div>
        )}
      </div>
    </PopoverContent>
  );
}
